/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { getUrlSync, IComponent, IComponentProps, IImageSettings, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ecomCartLoadState } from '../../../../utils/ecomcartloadstate';
import { cartScopeState } from '../../../../utils/cartscope';
import { findLinesTheVenueMustCorrect } from '../../../../utils/orderstate';
import { readLineAmountAtStorePrice, withStorePrices } from '../../../../utils/storetotals';
import { takeStoreOrderEndedMessage } from '../../../../utils/storeorderended';
import { storeOrderWatchState } from '../../../../utils/storeorderwatcher';
import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { reaction } from 'mobx';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { CartLine, DiscountLine, DiscountLineType, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { ArrayExtensions, getFallbackImageUrl, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ICartIconConfig, ICartIconResources } from '../../definition-extensions/cart-icon.ext.props.autogenerated';
import { ICartConfig, ICartResources } from '../../definition-extensions/cart.ext.props.autogenerated';
import MsDyn365, { Image } from '@msdyn365-commerce/core';
import { ICartLineProps } from './cartlineitem.component';
import { PriceComponent } from '@msdyn365-commerce/components';
import { CART_LINE_QUANTITY_DEBOUNCE_MS } from '../../../../utils/cartlinequantity';

/**
 * IBottomSummaryBarData: Interface for Bottom Summary Bar Data.
 */
export interface IBottomSummaryBarData {
    cart?: ICartState;
    products?: SimpleProduct[];
}

/**
 * IBottomSummaryBarComponentProps: Interface for Bottom Summary Bar component props.
 */
export interface IBottomSummaryBarComponentProps extends IComponentProps<IBottomSummaryBarData> {
    className?: string;
    telemetryContent?: ITelemetryContent;
    button: React.ReactNode;
    showFeesIncluded?: boolean;
    cartIconPropsConfig?: ICartConfig & ICartIconConfig & ICartIconConfig;
    cartIconPropsResources?: ICartResources & ICartIconResources;
}

/**
 * IBottomSummaryBarComponent: Interface for Bottom Summary Bar component props.
 */
export interface IBottomSummaryBarComponent extends IComponent<IBottomSummaryBarComponentProps> {}

const BottomSummaryBarComponentActions = {};

const FALLBACK_IMAGE =
    'https://images-us-prod.cms.commerce.dynamics.com/cms/api/sqwrjszgvs/imageFileData/search?fileName=/Products%2FZ0269_000_001.png';

const CustomQuantitySelector: React.FC<ICartLineProps> = props => {
    const {
        currentQuantity = 0,
        maxQuantity = 10,
        resources,
        shouldDisableButtons,
        shouldDisableDecrementButton,
        shouldDisableIncrementButton,
        quantityOnChange,
        createNewCartLineForSentToKitchen,
        data
    } = props;

    const isQuantityOne = currentQuantity === 1;
    const isDecrementDisabled = shouldDisableButtons || shouldDisableDecrementButton || currentQuantity <= 0;
    const isIncrementDisabled = shouldDisableButtons || shouldDisableIncrementButton || currentQuantity >= maxQuantity;

    const handleQuantityChange = (newQuantity: number): void => {
        if (quantityOnChange && newQuantity !== currentQuantity) {
            quantityOnChange(data.cartLine, newQuantity);
        }
    };

    const handleDecrement = (): void => {
        if (!isDecrementDisabled) {
            handleQuantityChange(currentQuantity - 1);
        }
    };

    const handleIncrement = (): void => {
        if (!isIncrementDisabled) {
            // If this is a sentToKitchen item, create new cart line instead of updating quantity
            if (shouldDisableDecrementButton && createNewCartLineForSentToKitchen) {
                createNewCartLineForSentToKitchen(data.cartLine);
            } else {
                handleQuantityChange(currentQuantity + 1);
            }
        }
    };

    const decrementAriaLabel = isQuantityOne
        ? resources.removeItemButtonAriaLabel || 'Remove item'
        : resources.decrementButtonAriaLabel || 'Decrease quantity';
    const decrementTitle = isDecrementDisabled ? '' : decrementAriaLabel;

    return (
        <div className='quantity'>
            <button
                type='button'
                className={`decrement quantity__controls ${isDecrementDisabled ? 'disabled' : ''} ${isQuantityOne ? 'delete-icon' : ''}`}
                onClick={handleDecrement}
                disabled={isDecrementDisabled}
                aria-label={decrementAriaLabel}
                title={decrementTitle}
            ></button>
            <input
                type='number'
                className='quantity-input'
                pattern='[0-9]*'
                value={currentQuantity}
                aria-live='polite'
                aria-label={`${resources.inputQuantityAriaLabel ?? 'quantity input'} ${currentQuantity}`}
                role='spinbutton'
                aria-valuemin={1}
                aria-valuemax={maxQuantity}
                aria-valuenow={currentQuantity}
                min={1}
                max={maxQuantity}
                readOnly
            />
            <button
                type='button'
                className={`increment quantity__controls ${isIncrementDisabled ? 'disabled' : ''}`}
                onClick={handleIncrement}
                disabled={isIncrementDisabled}
                aria-label={resources.incrementButtonAriaLabel}
                title={isIncrementDisabled ? '' : resources.incrementButtonAriaLabel}
            ></button>
        </div>
    );
};

const RenderDiscountLines: React.FC<ICartLineProps> = props => {
    const { cartLine } = props.data;

    if (!cartLine.DiscountLines || !ArrayExtensions.hasElements(cartLine.DiscountLines)) {
        return null;
    }

    /**
     * Get the updated discount percentage.
     */
    const discountPercent = (discountLine: DiscountLine, cartLine: CartLine): number => {
        const numberZero = 0;
        const decimalPrecision = 2;
        const fullPercentage = 100;

        let updatedPercent: number = discountLine.Percentage ?? numberZero;

        if (
            (discountLine.Percentage === numberZero ||
                (discountLine.DiscountLineTypeValue && discountLine.DiscountLineTypeValue === DiscountLineType.TenderTypeDiscount)) &&
            cartLine.Price &&
            cartLine.Price > numberZero &&
            cartLine.Quantity &&
            cartLine.Quantity !== numberZero
        ) {
            const effectiveAmount = discountLine.EffectiveAmount ?? numberZero;

            updatedPercent = Number(((effectiveAmount / (cartLine.Price * cartLine.Quantity)) * fullPercentage).toFixed(decimalPrecision));
        }

        return updatedPercent;
    };

    return (
        <>
            {cartLine.DiscountLines.map((discountLine, index) => (
                <div key={discountLine.OfferId ?? index} className='msc-cart-line__product-discount'>
                    <span>{discountLine.OfferName ?? ''}</span>

                    <span className='msc-cart-line-item-product-discount-price'>
                        <PriceComponent
                            data={{
                                price: {
                                    CustomerContextualPrice: discountLine.EffectiveAmount,
                                    BasePrice: discountLine.EffectiveAmount
                                }
                            }}
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                            currencyCode={props.priceCurrency}
                        />
                    </span>

                    <span className='msc-cart-line__product-discount-percentage'>
                        {` (${discountLine.Percentage !== undefined ? discountPercent(discountLine, cartLine) : ''}%)`}
                    </span>
                </div>
            ))}
        </>
    );
};

const OrderTotalSpinner: React.FC<{ label: string }> = ({ label }) => (
    <svg width='18' height='18' viewBox='0 0 44 44' role='img' aria-label={label} style={{ verticalAlign: 'middle' }}>
        <circle cx='22' cy='22' r='18' fill='none' stroke='currentColor' strokeOpacity='0.25' strokeWidth='6' />
        <path d='M22 4 a18 18 0 0 1 18 18' fill='none' stroke='currentColor' strokeWidth='6' strokeLinecap='round'>
            <animateTransform attributeName='transform' type='rotate' from='0 22 22' to='360 22 22' dur='0.9s' repeatCount='indefinite' />
        </path>
    </svg>
);

/** The bar's height, published on body for the page padding in hs-footer.scss. */
const BOTTOM_BAR_HEIGHT_PROPERTY = '--hs-bottom-bar-height';

const BottomSummaryBar: React.FC<IBottomSummaryBarComponentProps> = (props: IBottomSummaryBarComponentProps) => {
    const {
        data: { cart, products }
    } = props;

    const componentClassName = props.className ?? 'ms-cart';
    const miniCartDisclaimerText = props.cartIconPropsConfig?.miniCartDisclaimerText?.trim();

    const [showMiniCart, setShowMiniCart] = React.useState(false);
    const [animateOpen, setAnimateOpen] = React.useState(false);
    const [optimisticQuantities, setOptimisticQuantities] = React.useState<Record<string, number>>({});
    const [isUpdatingLine, setIsUpdatingLine] = React.useState<Record<string, boolean>>({});

    const openTimeoutRef = React.useRef<number | null>(null);
    const closeTimeoutRef = React.useRef<number | null>(null);
    const debounceTimersRef = React.useRef<Record<string, number>>({});
    const checkoutButtonsRef = React.useRef<HTMLDivElement>(null);
    const [checkoutButtonsHeight, setCheckoutButtonsHeight] = React.useState<number | undefined>(undefined);

    // The fixed bar covers the end of the page and of the drawer, so both keep the bar's height clear below them.
    React.useEffect(() => {
        const checkoutButtons = checkoutButtonsRef.current;
        if (!checkoutButtons || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const observer = new ResizeObserver(() => {
            setCheckoutButtonsHeight(checkoutButtons.offsetHeight);
            document.body.style.setProperty(BOTTOM_BAR_HEIGHT_PROPERTY, `${checkoutButtons.offsetHeight}px`);
        });
        observer.observe(checkoutButtons);
        return () => {
            observer.disconnect();
            document.body.style.removeProperty(BOTTOM_BAR_HEIGHT_PROPERTY);
        };
    }, []);

    const defaultCartItemCount = 0;
    // An absent cart and an empty cart both coalesce to zero below. This keeps them apart, so the bar
    // never claims the order is empty while the cart is still on its way.
    const isCartKnown = cart?.cart !== undefined;

    // A cart that exists but has not yet had the ecommerce order pulled into it looks exactly like an
    // empty one, so the bar waits for the pull to say it has finished before believing a zero.
    const [isEcomCartLoading, setIsEcomCartLoading] = React.useState(ecomCartLoadState.isLoading);

    // A basket that empties without a word is worse than the dead order it replaced, so whatever took the
    // guest off a finished order says so and the bar carries the message.
    const [storeOrderEndedMessage, setStoreOrderEndedMessage] = React.useState<string>('');

    React.useEffect(() => {
        setStoreOrderEndedMessage(takeStoreOrderEndedMessage() ?? '');
    }, []);

    React.useEffect(() => {
        return reaction(() => ecomCartLoadState.isLoading, setIsEcomCartLoading, { fireImmediately: true });
    }, []);

    const [isOrderChangedAtVenue, setIsOrderChangedAtVenue] = React.useState(storeOrderWatchState.changedAtVenue);

    React.useEffect(() => {
        return reaction(() => storeOrderWatchState.changedAtVenue, setIsOrderChangedAtVenue, { fireImmediately: true });
    }, []);

    const refreshOrder = React.useCallback(() => {
        window.location.reload();
    }, []);

    // The cart named by the cookie may be another customer's or another suite's until the header confirms it.
    const [isCartScopeSettled, setIsCartScopeSettled] = React.useState(cartScopeState.isSettled);

    React.useEffect(() => {
        return reaction(() => cartScopeState.isSettled, setIsCartScopeSettled, { fireImmediately: true });
    }, []);

    const isOrderKnown = isCartKnown && !isEcomCartLoading && isCartScopeSettled;
    const cartItemSubtotal = (cart?.cart ? withStorePrices(cart.cart).SubtotalAmount : undefined) ?? 0;
    const cartItem = cart?.totalItemsInCart ?? defaultCartItemCount;
    const cartLines = cart?.cart?.CartLines ?? [];

    const getCartLineKey = React.useCallback((line: CartLine): string => {
        return String(line.LineId ?? line.ItemId ?? line.ProductId ?? '');
    }, []);

    const clearOpenTimeout = React.useCallback(() => {
        if (openTimeoutRef.current !== null) {
            window.clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
        }
    }, []);

    const clearCloseTimeout = React.useCallback(() => {
        if (closeTimeoutRef.current !== null) {
            window.clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    }, []);

    const clearAllTimeouts = React.useCallback(() => {
        clearOpenTimeout();
        clearCloseTimeout();
    }, [clearOpenTimeout, clearCloseTimeout]);

    const getProductKey = React.useCallback((product?: SimpleProduct | null): string | undefined => {
        if (!product) {
            return undefined;
        }

        return String(product?.RecordId ?? '') || String(product?.ItemId ?? '') || undefined;
    }, []);

    const getLineProductKey = React.useCallback((line): string | undefined => {
        if (!line) {
            return undefined;
        }

        return String(line?.ProductId ?? line?.ItemId ?? '') || undefined;
    }, []);

    const productLookup = React.useMemo(() => {
        const lookup = new Map<string, SimpleProduct>();

        (products ?? []).forEach(product => {
            const key = getProductKey(product);

            if (key) {
                lookup.set(key, product);
            }
        });

        return lookup;
    }, [products, getProductKey]);

    const mergedCartLines = React.useMemo(() => {
        return cartLines.map(line => {
            const productKey = getLineProductKey(line);
            const matchedProduct = productKey ? productLookup.get(productKey) : undefined;

            return {
                line,
                product: matchedProduct
            };
        });
    }, [cartLines, productLookup, getLineProductKey]);

    const getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return (
            imageSettings || {
                viewports: {
                    xs: { q: 'w=80&h=94&m=2', w: 0, h: 0 },
                    sm: { q: 'w=148&h=174&m=2', w: 0, h: 0 },
                    lg: { q: 'w=148&h=174&m=2', w: 0, h: 0 }
                },
                lazyload: true
            }
        );
    };

    const openMiniCart = React.useCallback(() => {
        clearAllTimeouts();
        setShowMiniCart(true);

        openTimeoutRef.current = window.setTimeout(() => {
            setAnimateOpen(true);
            openTimeoutRef.current = null;
        }, 10);
    }, [clearAllTimeouts]);

    const closeMiniCart = React.useCallback(() => {
        clearAllTimeouts();
        setAnimateOpen(false);

        closeTimeoutRef.current = window.setTimeout(() => {
            setShowMiniCart(false);
            closeTimeoutRef.current = null;
        }, 320);
    }, [clearAllTimeouts]);

    const toggleMiniCart = React.useCallback(() => {
        if (showMiniCart) {
            closeMiniCart();
        } else {
            openMiniCart();
        }
    }, [showMiniCart, closeMiniCart, openMiniCart]);

    React.useEffect(() => {
        if (showMiniCart) {
            if (MsDyn365.isBrowser) {
                document.body.classList.add('mini-cart-open');
            }
        } else {
            if (MsDyn365.isBrowser) {
                document.body.classList.remove('mini-cart-open');
            }
        }

        return () => {
            if (MsDyn365.isBrowser) {
                document.body.classList.remove('mini-cart-open');
            }
        };
    }, [showMiniCart]);

    React.useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && showMiniCart) {
                closeMiniCart();
            }
        };

        if (MsDyn365.isBrowser) {
            document.addEventListener('keydown', onKeyDown);
        }

        return () => {
            if (MsDyn365.isBrowser) {
                document.removeEventListener('keydown', onKeyDown);
            }
        };
    }, [showMiniCart, closeMiniCart]);

    React.useEffect(() => {
        const syncedQuantities: Record<string, number> = {};
        cartLines.forEach(line => {
            syncedQuantities[getCartLineKey(line)] = Number(line.Quantity ?? 0);
        });
        setOptimisticQuantities(syncedQuantities);
    }, [cartLines, getCartLineKey]);

    React.useEffect(() => {
        return () => {
            clearAllTimeouts();
            Object.values(debounceTimersRef.current).forEach(timerId => window.clearTimeout(timerId));
            debounceTimersRef.current = {};
            if (MsDyn365.isBrowser) {
                document.body.classList.remove('mini-cart-open');
            }
        };
    }, [clearAllTimeouts]);

    const getProductName = (line: CartLine, product?: SimpleProduct) => {
        return product?.Name || line?.Description || 'Cart item';
    };

    const linesTheVenueMustCorrect = findLinesTheVenueMustCorrect(cart?.cart);
    const linesTheVenueMustCorrectMessage = linesTheVenueMustCorrect.length
        ? (props.cartIconPropsResources?.linesTheVenueMustCorrectText ?? '').replace(
              '{items}',
              linesTheVenueMustCorrect.map(line => getProductName(line, productLookup.get(getLineProductKey(line) ?? ''))).join(', ')
          )
        : '';

    const getProductUrl = (product?: SimpleProduct, cartLine?: CartLine) => {
        let productUrl: string | undefined;
        productUrl = getUrlSync('product', props.context?.actionContext, {
            category: {
                Url: `product-details/${product?.ItemId}`,
                Slug: `product-details/${product?.ItemId}`
            }
        });
        console.log(productUrl, 'vivz');
        return productUrl || '#';
    };

    const getProductImage = (product?: SimpleProduct) => {
        return product?.PrimaryImageUrl || FALLBACK_IMAGE;
    };

    const onMiniCartQuantityChange = React.useCallback(
        (cartLine: CartLine, newQuantity: number): boolean => {
            const lineKey = getCartLineKey(cartLine);
            setOptimisticQuantities(prev => ({ ...prev, [lineKey]: newQuantity }));

            const activeTimer = debounceTimersRef.current[lineKey];
            if (activeTimer) {
                window.clearTimeout(activeTimer);
            }

            debounceTimersRef.current[lineKey] = window.setTimeout(() => {
                const syncQuantity = async () => {
                    setIsUpdatingLine(prev => ({ ...prev, [lineKey]: true }));
                    const cartState = await getCartState(props.context.actionContext);
                    if (newQuantity <= 0) {
                        const lineId = cartLine.LineId?.toString();
                        if (lineId) {
                            await cartState.removeCartLines({ cartLineIds: [lineId] });
                        }
                        return;
                    }

                    const updatedLine: CartLine = {
                        ...cartLine,
                        Quantity: newQuantity
                    };
                    await cartState.updateCartLines({ updatedCartLinesObject: [updatedLine] });
                };

                syncQuantity()
                    .catch(error => {
                        console.error('Error updating mini-cart quantity:', error);
                    })
                    .finally(() => {
                        setIsUpdatingLine(prev => ({ ...prev, [lineKey]: false }));
                    });
            }, CART_LINE_QUANTITY_DEBOUNCE_MS);
            return true;
        },
        [getCartLineKey, props.context.actionContext]
    );

    const onMiniCartCreateLineForSentToKitchen = React.useCallback((cartLine: CartLine): void => {
        if (!MsDyn365.isBrowser) {
            return;
        }
        window.dispatchEvent(
            new CustomEvent('hs-cart-line-create-for-sent-to-kitchen', {
                detail: { cartLine }
            })
        );
    }, []);

    const renderMiniCartLines = () => {
        if (!mergedCartLines.length) {
            return (
                <div className='mini-cart-drawer__empty'>
                    <div className='mini-cart-drawer__empty-title'>Your cart is empty</div>
                    <div className='mini-cart-drawer__empty-text'>Add items to see them here.</div>
                </div>
            );
        }

        return mergedCartLines.map(({ line, product }, index: number) => {
            const productName = getProductName(line, product);
            const productUrl = getProductUrl(product, line);
            const imageUrl = getProductImage(product);

            const getMenuType = (): string | null => {
                const menuIdProperty = line.ExtensionProperties?.find(prop => prop.Key === 'ITSMenuId');
                return menuIdProperty?.Value?.StringValue || null;
            };

            const menuType = getMenuType();

            const fallbackImageUrl =
                product &&
                getFallbackImageUrl(
                    product.ItemId,
                    props.context.actionContext.requestContext.apiSettings,
                    props.context.app?.config?.OmniChannelMedia
                );
            const imageSettings = getImageSettings(props.cartIconPropsConfig?.imageSettings);
            imageSettings.cropFocalRegion = !ObjectExtensions.isNullOrUndefined(imageSettings);

            const lineKey = getCartLineKey(line);
            const quantity = Number(optimisticQuantities[lineKey] ?? line?.Quantity ?? 0);
            const amountAtStorePrice = readLineAmountAtStorePrice(line);
            const adjustedPrice = Number(amountAtStorePrice ?? line?.ExtendedPrice ?? line?.NetAmountWithoutTax ?? 0);
            const basePrice = Number(amountAtStorePrice ?? line?.Price ?? adjustedPrice);
            const hasDiscount = basePrice > adjustedPrice;

            return (
                <div className='mini-cart-line-wrapper'>
                    <div className='mini-cart-line' key={line?.LineId ?? line?.ItemId ?? `${productName}-${index}`}>
                        <a className='mini-cart-line__image-wrap' href={productUrl} aria-label={productName} role='link'>
                            {MsDyn365.isBrowser ? (
                                <Image
                                    requestContext={props.context.actionContext.requestContext}
                                    aria-hidden='true'
                                    src={imageUrl ?? 'empty'}
                                    fallBackSrc={fallbackImageUrl}
                                    altText={productName}
                                    gridSettings={props.context.request.gridSettings ?? {}}
                                    imageSettings={imageSettings}
                                    loadFailureBehavior='empty'
                                />
                            ) : null}
                            {/* <span className='mini-cart-line__qty-badge'>{quantity}</span> */}
                        </a>

                        <div className='mini-cart-line__content'>
                            {/* @ts-ignore */}

                            <a className='mini-cart-line__title' href={productUrl}>
                                {productName}
                            </a>
                            {menuType && <div className='mini-cart-line__menu-type-badge'>{menuType}</div>}

                            <div className='mini-cart-line__price-block'>
                                {hasDiscount && (
                                    <div className='mini-cart-line__price-old'>
                                        {props.context.cultureFormatter.formatCurrency(basePrice)}
                                    </div>
                                )}
                                <div className='mini-cart-line__price-current'>
                                    {props.context.cultureFormatter.formatCurrency(adjustedPrice)}
                                </div>
                                {/* <div className='msc-cart-line__product-price'>
                                    <PriceComponent
                                        data={{
                                            price: {
                                                CustomerContextualPrice: line.ExtendedPrice,
                                                BasePrice: line.NetPrice
                                            }
                                        }}
                                        context={props.context}
                                        id={props.id}
                                        typeName={props.typeName}
                                        className='msc-cart-line__product-discount-value'
                                        currencyCode={'$'}
                                    />
                                </div> */}
                            </div>

                            <div className='mini-cart-line__meta'>Qty: {quantity}</div>
                            <RenderDiscountLines
                                context={props.context}
                                gridSettings={props.context.request.gridSettings || {}}
                                imageSettings={props.cartIconPropsConfig?.imageSettings!}
                                resources={props.cartIconPropsResources as any}
                                id={props.id}
                                typeName={props.typeName}
                                data={{ cartLine: line, product: product }}
                            />
                        </div>
                    </div>
                    <div className='mini-cart-line__qty-selector'>
                        {/* @ts-ignore */}
                        <CustomQuantitySelector
                            currentQuantity={quantity}
                            maxQuantity={10}
                            resources={props.cartIconPropsResources as any}
                            shouldDisableButtons={!!isUpdatingLine[lineKey]}
                            shouldDisableDecrementButton={false}
                            shouldDisableIncrementButton={false}
                            quantityOnChange={onMiniCartQuantityChange}
                            createNewCartLineForSentToKitchen={onMiniCartCreateLineForSentToKitchen}
                            data={{ cartLine: line }}
                        />
                    </div>
                </div>
            );
        });
    };

    return (
        <>
            <div className={`${componentClassName}__checkout-buttons`} ref={checkoutButtonsRef}>
                <div className={`${componentClassName}__checkout-wrapper`}>
                    <div className={`${componentClassName}__mini-cart-wrapper`}>
                        {isOrderKnown && miniCartDisclaimerText ? <p className='mini-cart-disclaimer'>{miniCartDisclaimerText}</p> : null}

                        {isOrderKnown ? (
                            <div className={`mini-cart-trigger-wrapper ${showMiniCart ? 'show' : 'hidden'}`}>
                                <button
                                    type='button'
                                    onClick={toggleMiniCart}
                                    className='mini-cart-trigger'
                                    aria-expanded={showMiniCart}
                                    aria-controls='mini-cart-drawer'
                                >
                                    {/* <span className='mini-cart-trigger-text'>{!showMiniCart ? 'View items' : 'Hide items'}</span> */}
                                    <span className='mini-cart-trigger__icon'>
                                        {cartItem > 0 && <span className='mini-cart-trigger__count'>{Number(cartItem)}</span>}
                                    </span>
                                </button>
                            </div>
                        ) : null}
                    </div>

                    {storeOrderEndedMessage ? (
                        <div className={`${componentClassName}__store-order-ended`} role='status'>
                            {storeOrderEndedMessage}
                        </div>
                    ) : null}

                    {isOrderChangedAtVenue ? (
                        <div className={`${componentClassName}__order-changed-at-venue`} role='status'>
                            <span>{props.cartIconPropsResources?.storeOrderChangedAtVenueText}</span>
                            <button
                                type='button'
                                className={`${componentClassName}__order-changed-at-venue-refresh`}
                                onClick={refreshOrder}
                            >
                                {props.cartIconPropsResources?.refreshOrderButtonText}
                            </button>
                        </div>
                    ) : null}

                    {linesTheVenueMustCorrectMessage ? (
                        <div className={`${componentClassName}__lines-the-venue-must-correct`} role='status'>
                            {linesTheVenueMustCorrectMessage}
                        </div>
                    ) : null}

                    <div className={`${componentClassName}__checkout-info-parent`}>
                        <div className={`${componentClassName}__checkout-info`}>
                            <div className={`${componentClassName}__checkout-info__total-count`}>
                                {isOrderKnown ? `Total: ${Number(cartItem)} Items` : 'Loading your order'}
                            </div>

                            <div className={`${componentClassName}__checkout-info__total-amount`}>
                                {isOrderKnown ? (
                                    <span className='msc-order-summary__value'>
                                        {props.context.cultureFormatter.formatCurrency(Number(cartItemSubtotal))}
                                    </span>
                                ) : (
                                    <OrderTotalSpinner label='Loading your order total' />
                                )}
                            </div>

                            {props.showFeesIncluded && (
                                <div className={`${componentClassName}__checkout-info__additional-info`}>Fees included</div>
                            )}
                        </div>

                        <div className={`${componentClassName}__checkout-info__button`}>{isOrderKnown ? props.button : null}</div>
                    </div>
                </div>
            </div>

            {showMiniCart && isOrderKnown && (
                <div className={`mini-cart-overlay ${animateOpen ? 'open' : ''}`} onClick={closeMiniCart} aria-hidden='true'>
                    <div
                        id='mini-cart-drawer'
                        className={`mini-cart-drawer ${animateOpen ? 'open' : ''}`}
                        onClick={e => e.stopPropagation()}
                        role='dialog'
                        aria-modal='true'
                        aria-label='Mini cart'
                    >
                        <div className='mini-cart-drawer__handle' onClick={closeMiniCart} />

                        <div className='mini-cart-drawer__header'>
                            <div>
                                <div className='mini-cart-drawer__title'>Your order</div>
                                <div className='mini-cart-drawer__subtitle'>{isOrderKnown ? `${Number(cartItem)} item(s)` : 'Loading'}</div>
                            </div>
                            {/* UA: Close button - enable if required, otherwise close happens on trigger button, top handle and backdrop press */}
                            {/* <button
                                type='button'
                                className='mini-cart-drawer__close'
                                onClick={closeMiniCart}
                                aria-label='Close mini cart'
                            /> */}
                        </div>

                        <div className='mini-cart-drawer__body' style={{ marginBottom: checkoutButtonsHeight }}>
                            {renderMiniCartLines()}
                        </div>

                        {/* <div className='mini-cart-drawer__footer'>
                            <div className='mini-cart-drawer__summary'>
                                <div className='mini-cart-drawer__summary-label'>Total</div>
                                <div className='mini-cart-drawer__summary-value'>
                                    {isOrderKnown ? `$${Number(cartItemSubtotal).toFixed(2)}` : <OrderTotalSpinner label='Loading your order total' />}
                                </div>
                            </div>

                            <div className='mini-cart-drawer__footer-button'>{props.button}</div>
                        </div> */}
                    </div>
                </div>
            )}
        </>
    );
};

export const BottomSummaryBarComponent: React.FunctionComponent<IBottomSummaryBarComponentProps> = msdyn365Commerce.createComponentOverride<
    // @ts-expect-error -- Compatible issue with the component override.
    IBottomSummaryBarComponent
>('BottomSummaryBar', { component: BottomSummaryBar, ...BottomSummaryBarComponentActions });

export default BottomSummaryBarComponent;
